'use client';

import { useEffect, useState } from 'react';

import classNames from 'classnames';
import DOMPurify from 'isomorphic-dompurify';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ArrowRight2 } from '@uikit/icons/ArrowRight2';
import MarkInfoIcon from '@uikit/icons/MarkInfo';

import type {
  RewardPool,
  RewardPoolDistributionPolicy} from '@shared/api/types/rewardPools';
import {
  REWARD_POOL_DISTRIBUTION_POLICY
} from '@shared/api/types/rewardPools';
import BaseInfoTooltip from '@shared/common/components/BaseInfoTooltip';
import NextLink from '@shared/common/components/NextLink';
import { useAuth } from '@shared/common/providers/AuthProvider';
import { formatNumberGroupThousands } from '@shared/helpers/number';
import {
  useRewardPoolQuery,
  useSelfRewardPoolLeaderboardDataQuery,
} from '@shared/hooks/query/rewardPools';

import { RewardOptions } from '@store:web/components/entities/rewardPool';
import { useWatchBuyEntries } from '@store:web/components/entities/rewardPool';
import { cn } from '@ui/uikit/utils';

import { JoinRewardPoolButton } from '../RewardPoolsPage/common/RewardPoolCard/JoinRewardPoolButton';
import RewardPoolCardParticipationTag from '../RewardPoolsPage/common/RewardPoolCard/RewardPoolCardParticipationTag';
import { getRewardPoolReward } from '../RewardPoolsPage/common/RewardPoolCard/RewardPoolCardReward';
import { RewardPoolCardTags } from '../RewardPoolsPage/common/RewardPoolCard/RewardPoolCardTags';
import RewardPoolLeaderboardDialog from './RewardPoolLeaderboardDialog';

const DISTRIBUTION_POLICY_INFO: Record<
  RewardPoolDistributionPolicy,
  { name: string; description: string }
> = {
  [REWARD_POOL_DISTRIBUTION_POLICY.FCFS]: {
    name: 'FCFS',
    description:
      'The FCFS V2 pool operates similarly to the ordinary FCFS pool, with one key difference: the rewards are distributed immediately after a user joins the pool. There is still a limit on the number of participants, and once this limit is reached, no new users can join.',
  },
  [REWARD_POOL_DISTRIBUTION_POLICY.FCFS_V2]: {
    name: 'FCFS',
    description:
      'The FCFS V2 pool operates similarly to the ordinary FCFS pool, with one key difference: the rewards are distributed immediately after a user joins the pool. There is still a limit on the number of participants, and once this limit is reached, no new users can join.',
  },
  [REWARD_POOL_DISTRIBUTION_POLICY.PRO_RATA_KARMA]: {
    name: 'Pro-Rata',
    description:
      'In karma-based Pro-Rata pools, the total reward pool is divided based on the amount of Karma each participant has earned during the pool’s duration, regardless of when they joined. The more Karma you accumulate, the larger your share of the rewards.',
  },
  [REWARD_POOL_DISTRIBUTION_POLICY.PRO_RATA_SWAP]: {
    name: 'Pro-Rata',
    description:
      'In swap-based Pro-Rata pools, the total reward pool is divided based on each participant’s USD-equivalent Swap Volume accumulated after joining the pool. The more you swap, the larger your share of the rewards.',
  },
  [REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE]: {
    name: 'Raffle',
    description:
      'In a Raffle pool, the winners are chosen randomly from all the users who have joined the pool. This policy gives every participant an equal chance of winning, regardless of the amount of Karma earned or the time of joining. Rewards will be distributed once all spots are filled. If not, they will be distributed when the pool time expires.',
  },
  [REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE_V2]: {
    name: 'Raffle',
    description:
      'In a Raffle pool, the winners are chosen randomly from all the users who have joined the pool. Rewards will be distributed once all spots are filled. If not, they will be distributed when the pool time expires.',
  },
  [REWARD_POOL_DISTRIBUTION_POLICY.RAFFLE_SWAP]: {
    name: 'Raffle',
    description:
      'In a swap-based Raffle pools, the winners are chosen randomly, but each participant’s chance of winning depends on the number of tickets they’ve earned. Tickets are granted based on the participant’s USD-equivalent Swap Volume accumulated after joining the pool. The more you swap, the more tickets you earn — and the higher your chances of winning. Rewards will be distributed once all spots are filled, or when the pool time expires.',
  },
};

const RewardPoolPage = ({ initialRewardPool }: { initialRewardPool: RewardPool }) => {
  const { data: rewardPool = initialRewardPool } = useRewardPoolQuery(initialRewardPool.id);
  const [showClaimedByMe, setShowClaimedByMe] = useState(false);
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);
  const { isAuth } = useAuth();
  const { data: leaderboardSelf, refetch: refetchLeaderboardSelf } =
    useSelfRewardPoolLeaderboardDataQuery(rewardPool.id, {
      enabled: isAuth,
    });

  useWatchBuyEntries({ type: 'item' });

  const selfReward = leaderboardSelf?.reward;
  const isClaimedByMeButtonDisabled = !selfReward;
  const {
    formattedReward,
    formattedSelfReward,
    rewardPerWinner,
    Icon,
    iconClassName,
    id,
    bgClassName,
    borderColorClassName,
  } = getRewardPoolReward({
    rewardPool,
    selfReward,
  });

  // toggle back to all rewards
  useEffect(() => {
    if (!selfReward) {
      setShowClaimedByMe(false);
    }
  }, [selfReward]);

  const participantsCount = rewardPool.participants_counter || 0;
  const isViewParticipantsButtonDisabled = participantsCount === 0;

  const policyInfo = DISTRIBUTION_POLICY_INFO[rewardPool.distribution_policy] || {
    name: 'N/A',
    description: 'N/A',
  };

  const subLabel = showClaimedByMe ? 'Reward' : rewardPerWinner;
  const label = showClaimedByMe ? formattedSelfReward : formattedReward;

  return (
    <>
      <RewardPoolLeaderboardDialog
        rewardPool={rewardPool}
        onOpenChange={setIsLeaderboardOpen}
        open={isLeaderboardOpen}
      />

      <div className="relative">
        <div className="absolute w-full top-0 left-0 h-56 gradient-page -z-[1]" />
        <main className="msq-container pt-6 xl:pt-10">
          <nav
            aria-label="Breadcrumb"
            className="breadcrumbs breadcrumbs-sm mb-2 xl:mb-1 xl:breadcrumbs-md"
          >
            <NextLink href="/" className="breadcrumbs-link">
              Discover
            </NextLink>
            <ArrowRight2 aria-hidden="true" className="breadcrumbs-separator" />
            <NextLink href="/reward-pools" className="breadcrumbs-link">
              Reward Pools
            </NextLink>
            <ArrowRight2 aria-hidden="true" className="breadcrumbs-separator" />
            <NextLink
              href={`/reward-pools/${rewardPool.id}`}
              className="breadcrumbs-link"
              aria-current="page"
            >
              {rewardPool.name}
            </NextLink>
          </nav>

          <header className="flex max-xl:flex-col xl:items-center xl:justify-between gap-4 mb-6 xl:mb-8">
            <h1 className="heading-h1-adaptive">{rewardPool.name}</h1>
          </header>

          <div className="flex gap-8 max-2xl:flex-col 2xl:items-start xl:gap-[3.375rem] 2xl:gap-16">
            <div
              className={classNames(
                'rounded-3xl flex flex-col gap-4 xl:gap-6 w-full border border-base-border-medium p-4 xl:p-6 2xl:max-w-[25rem]',
                // size of two reward cards
                'xl:max-w-[calc(50%-0.5rem)]',
              )}
            >
              <RewardPoolCardParticipationTag className="mb-2" rewardPool={rewardPool} />
              <RewardPoolCardTags rewardPool={rewardPool} />

              {rewardPool.description && (
                <p
                  className="font-medium text-sm leading-6 text-[#373743] inner-html"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rewardPool.description) }}
                />
              )}

              <div className="flex max-md:flex-col xl:flex-col gap-3">
                <button
                  onClick={() => setIsLeaderboardOpen(true)}
                  className="msq-btn w-full msq-btn-lg msq-btn-secondary"
                  disabled={isViewParticipantsButtonDisabled}
                  data-qa="participants_button"
                >
                  {participantsCount === 0 && 'No Participants'}
                  {participantsCount > 0 && (
                    <>
                      View {formatNumberGroupThousands(participantsCount)} Participant
                      {participantsCount > 1 ? 's' : ''}
                    </>
                  )}
                </button>

                <JoinRewardPoolButton rewardPool={rewardPool} onSuccess={refetchLeaderboardSelf} />
              </div>
            </div>

            <div>
              <div className="flex max-xl:flex-col gap-4 mb-6 xl:mb-8 xl:justify-between">
                <h3 className="heading-h2-adaptive">
                  {policyInfo.name} Pool Rewards
                  <BaseInfoTooltip
                    icon={
                      <span className="flex text-[#C2C2D6] flex-shrink-0 ml-1 items-center justify-center size-5 xl:size-6">
                        <MarkInfoIcon className="icon-xs" />
                      </span>
                    }
                    content={policyInfo.description}
                  />
                </h3>

                <div className="toggle-button-group whitespace-nowrap w-full md:max-w-[22.5rem] xl:max-w-min">
                  <button
                    disabled={isClaimedByMeButtonDisabled}
                    onClick={() => setShowClaimedByMe(true)}
                    data-qa="claimed_by_me_button"
                    className={classNames('toggle-button flex-1', {
                      'toggle-button-active': showClaimedByMe,
                    })}
                  >
                    My Reward
                  </button>
                  <button
                    onClick={() => setShowClaimedByMe(false)}
                    data-qa="total_reward_button"
                    className={classNames('toggle-button flex-1', {
                      'toggle-button-active': !showClaimedByMe,
                    })}
                  >
                    Total Rewards
                  </button>
                </div>
              </div>

              <Swiper
                className={classNames(
                  'mb-8 xl:mb-14 -mx-4 max-md:px-4 md:-mx-2 line-swiper-pagination',
                )}
                modules={[Pagination]}
                pagination={{
                  enabled: true,
                  clickable: true,
                }}
                slidesPerView="auto"
              >
                <SwiperSlide
                  key={id}
                  className={classNames(
                    'select-none px-1 w-1/2 sm:w-1/4 md:px-2 max-md:first:pl-0 max-md:last:pr-0',
                  )}
                >
                  <div
                    className={classNames(
                      'border flex flex-col gap-4 px-6 pt-5 pb-4 rounded-xl xl:px-8 xl:pt-6 xl:pb-5',
                      borderColorClassName,
                    )}
                  >
                    <span
                      className={classNames(
                        'rounded-md flex items-center justify-center w-full aspect-square',
                        bgClassName,
                      )}
                    >
                      {Icon && <Icon className={cn('size-[42.5%]', iconClassName)} />}
                    </span>

                    <div
                      title={label ? String(label) : ''}
                      className="flex flex-col items-center justify-start text-center max-xl-msq:h-[56px] h-[68px]"
                    >
                      <span
                        className="font-semibold text-sm xl:text-base line-clamp-2"
                        data-qa="reward_amount"
                      >
                        {label}
                      </span>
                      {subLabel && (
                        <div className="text-base-text-tertiary font-medium text-xs xl:text-sm ">
                          {subLabel}
                        </div>
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              </Swiper>
              <div className="flex flex-col gap-8">
                <RewardOptions rewardPool={rewardPool} />
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default RewardPoolPage;
